import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import TurnstileWidget from '@/components/common/TurnstileWidget';
import { 
  Megaphone, Users, TrendingUp, Target, CheckCircle, ArrowRight, 
  Phone, Building2, Eye, BarChart3, Mail, Globe, Loader2
} from 'lucide-react';
import { useAddonPricing } from '@/hooks/useAddonPricing';

// Ad format config (prices fetched dynamically based on boost tiers)
const adFormatConfig = [
  { 
    icon: Building2, 
    title: 'Premium Listings', 
    description: 'Feature your properties at the top of search results',
    priceKey: 'standard',
    pricePrefix: 'From'
  },
  { 
    icon: Eye, 
    title: 'Banner Ads', 
    description: 'High-visibility banners across our platform',
    priceKey: 'premium',
    pricePrefix: 'From'
  },
  { 
    icon: Target, 
    title: 'Targeted Campaigns', 
    description: 'Reach specific buyer demographics and locations',
    priceKey: null,
    staticPrice: 'Custom pricing'
  },
  { 
    icon: Mail, 
    title: 'Email Sponsorship', 
    description: 'Get featured in our newsletter to 50K+ subscribers',
    priceKey: 'elite',
    pricePrefix: 'From'
  },
];

const stats = [
  { value: '100K+', label: 'Monthly Visitors' },
  { value: '25K+', label: 'Registered Users' },
  { value: '5M+', label: 'Property Views' },
  { value: 'Pan India', label: 'Coverage' },
];

const benefits = [
  'Reach qualified buyers and renters actively searching',
  'Targeted advertising to specific locations and budgets',
  'Detailed analytics and performance tracking',
  'Dedicated account manager for premium advertisers',
  'Multi-platform exposure (web, mobile, email)',
  'Flexible pricing to suit any budget',
];

const Advertise = () => {
  const [loading, setLoading] = useState(false);
  const { boostTiers, loading: pricingLoading } = useAddonPricing();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    adType: '',
    budget: '',
    message: ''
  });
  
  // Build ad formats with dynamic pricing
  const adFormats = adFormatConfig.map(format => ({
    icon: format.icon,
    title: format.title,
    description: format.description,
    price: format.priceKey 
      ? `${format.pricePrefix} ₹${(boostTiers?.[format.priceKey]?.price || 2999).toLocaleString('en-IN')}/listing`
      : format.staticPrice
  }));

  // WhatsApp helper - tries wa.me first, falls back to tel: if blocked
  const openWhatsApp = (message) => {
    const phoneNumber = '918790460259';
    const encodedMessage = encodeURIComponent(message);
    
    // Try opening WhatsApp - if it fails, user can call directly
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    
    // If popup was blocked or failed, show toast with phone option
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      toast.info(
        <div>
          <p>WhatsApp couldn't open. Call us directly:</p>
          <a href="tel:+918790460259" className="text-primary font-bold">+91 87904 60259</a>
        </div>
      );
    }
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[^0-9+\-\s()]/g, '').replace(/\s/g, '');
    return cleaned.length >= 10;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!isValidPhone(formData.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    
    try {
      const response = await apiClient.post('/leads/', {
        business_line: 'advertising',
        name: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        source: 'website',
        captcha_token: captchaToken,
        metadata: {
          company_name: formData.companyName,
          ad_type: formData.adType,
          budget: formData.budget,
          website: formData.website
        }
      });
      
      toast.success(`Advertising inquiry submitted! Request ID: ${response.data.request_id}. Our team will contact you within 24-48 hours.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'CAPTCHA_VALIDATION_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
        setCaptchaToken(null);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="advertise-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Megaphone className="w-4 h-4" /> Advertising Solutions
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Reach <span className="text-primary">Property Seekers</span> Where They Search
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Promote your properties, real estate services, or home-related business to our 
                growing audience of active buyers, renters, and homeowners across India.
              </p>
              <div className="flex gap-4 flex-wrap items-center">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary px-8"
                  onClick={() => document.getElementById('advertise-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => openWhatsApp("Hi ZenStudios! I'm interested in advertising opportunities.")}
                >
                  <Phone className="w-5 h-5 mr-2" /> WhatsApp Us
                </Button>
                <a href="tel:+918790460259" className="text-gray-600 hover:text-primary text-sm font-medium">
                  or call +91 87904 60259
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary to-green-600 rounded-2xl p-8 text-white">
                <Globe className="w-16 h-16 mb-6 opacity-80" />
                <h3 className="text-2xl font-bold mb-4">Why Advertise with ZenStudios?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> India's growing property platform
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> High-intent audience ready to transact
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> Transparent pricing & measurable ROI
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-white/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Formats */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advertising Options</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose from a variety of ad formats to reach your target audience effectively
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adFormats.map((format, idx) => (
              <div key={idx} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <format.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{format.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{format.description}</p>
                <p className="text-primary font-semibold">{format.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Grow Your Business With Us</h2>
              <p className="text-gray-600 mb-8">
                Our platform connects you with serious property seekers who are actively looking 
                to buy, sell, or rent. Turn impressions into leads and leads into customers.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Performance Dashboard</h3>
                  <p className="text-sm text-gray-500">Track your campaigns in real-time</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Impressions</span>
                  <span className="font-bold text-primary">125,430</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Clicks</span>
                  <span className="font-bold text-primary">3,421</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Leads Generated</span>
                  <span className="font-bold text-primary">156</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-600">Cost per Lead</span>
                  <span className="font-bold text-green-600">₹64</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20" id="advertise-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Start Advertising Today</h2>
            <p className="text-gray-600">
              Fill out the form below and our advertising team will get in touch
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg border">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Company/Business Name *</Label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    placeholder="Your company name"
                    required
                    className="rounded-xl mt-1"
                    data-testid="advertise-company-input"
                  />
                </div>
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                    placeholder="Your name"
                    className="rounded-xl mt-1"
                    data-testid="advertise-contact-input"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@company.com"
                    required
                    className="rounded-xl mt-1"
                    data-testid="advertise-email-input"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                    data-testid="advertise-phone-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Interested In</Label>
                  <Select value={formData.adType} onValueChange={(v) => setFormData({...formData, adType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue placeholder="Select ad type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="premium_listing">Premium Listings</SelectItem>
                      <SelectItem value="banner_ads">Banner Ads</SelectItem>
                      <SelectItem value="targeted_campaign">Targeted Campaigns</SelectItem>
                      <SelectItem value="email_sponsorship">Email Sponsorship</SelectItem>
                      <SelectItem value="custom">Custom Solution</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monthly Budget</Label>
                  <Select value={formData.budget} onValueChange={(v) => setFormData({...formData, budget: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="below_5k">Below ₹5,000</SelectItem>
                      <SelectItem value="5k_15k">₹5,000 - ₹15,000</SelectItem>
                      <SelectItem value="15k_50k">₹15,000 - ₹50,000</SelectItem>
                      <SelectItem value="50k_plus">₹50,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Tell us about your advertising goals</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="What are you looking to promote? Who is your target audience?"
                  rows={4}
                  className="rounded-xl mt-1"
                  data-testid="advertise-message-input"
                />
              </div>

              <TurnstileWidget
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
                className="flex justify-center"
              />

              <Button 
                type="submit" 
                className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" 
                disabled={loading || !captchaToken}
                data-testid="advertise-submit-btn"
              >
                {loading ? 'Submitting...' : 'Request Advertising Info'}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Our sales team will contact you within 24 hours to discuss your needs.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Advertise;
